"use client";

import { cn } from "@/lib/utils";
import useTranslate from "@app/_hooks/useTranslate";
import { Button } from "@ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSet,
} from "@ui/field";
import { Heading } from "@ui/heading";
import { Paragraph } from "@ui/paragraph";
import { Loader2 } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState, type ComponentProps } from "react";
import useAuth from "../hooks/useAuth";
import useConfirmPasswordReset from "../hooks/useConfirmPasswordReset";
import useConfirmPasswordResetForm, {
  PASSWORD_RESET_SIGN_IN_FAILED,
} from "../hooks/useConfirmPasswordResetForm";
import getFieldErrorState from "../utils/getFieldErrorState";
import ForgottenPasswordForm from "./ForgottenPasswordForm";
import SignInForm from "./SignInForm";

function ResetPasswordSpinner({ className }: { className?: string }) {
  return (
    <div
      className={cn("flex flex-col", className)}
      role="status"
      aria-busy="true"
    >
      <Loader2 className="size-6 animate-spin" aria-hidden="true" />
    </div>
  );
}

function PageHeading(props: ComponentProps<typeof Heading>) {
  return <Heading as="h1" {...props} />;
}

function ResetPasswordSignInFallback({ email }: { email: string }) {
  const t = useTranslate();

  return (
    <>
      <Heading className="mb-8 text-2xl" as="h1">
        {t("newPasswordSaved")}
      </Heading>
      <Paragraph role="status">{t("resetPasswordLoginText")}</Paragraph>
      <SignInForm className="mt-8" defaultEmail={email} />
    </>
  );
}

function ResetPasswordFields({
  oobCode,
  email,
}: {
  oobCode: string;
  email: string;
}) {
  const t = useTranslate();
  const form = useConfirmPasswordResetForm({ oobCode, email });

  return (
    <form.Subscribe
      selector={(state) => state.errors.includes(PASSWORD_RESET_SIGN_IN_FAILED)}
      children={(signInFailed) =>
        signInFailed ? (
          <ResetPasswordSignInFallback email={email} />
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              form.handleSubmit();
            }}
          >
            <Heading className="mb-8 text-2xl" as="h1">
              {t("formConfirmNewPasswordButton")}
            </Heading>
            <FieldSet>
              <FieldGroup className="md:flex-row">
                <form.AppField
                  name="password"
                  children={(field) => (
                    <Field
                      invalid={
                        field.getMeta().isDirty && !field.getMeta().isValid
                      }
                      dirty={field.getMeta().isDirty}
                      touched={field.getMeta().isTouched}
                    >
                      <FieldLabel placeholder>
                        {t("formPasswordLabel")}
                      </FieldLabel>
                      <field.Input
                        required
                        autoComplete="new-password"
                        placeholder={t("formPasswordLabel")}
                        type="password"
                        value={field.state.value}
                        onChange={(event) =>
                          field.handleChange(event.target.value)
                        }
                        onBlur={() => field.handleBlur()}
                      />
                      {getFieldErrorState(field) && (
                        <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                      )}
                      <FieldDescription>
                        {t("passwordRequirements")}
                      </FieldDescription>
                    </Field>
                  )}
                />
                <form.AppField
                  name="repeatPassword"
                  children={(field) => (
                    <Field
                      invalid={
                        field.getMeta().isDirty && !field.getMeta().isValid
                      }
                      dirty={field.getMeta().isDirty}
                      touched={field.getMeta().isTouched}
                    >
                      <FieldLabel placeholder>
                        {t("formRepeatPasswordLabel")}
                      </FieldLabel>
                      <field.Input
                        required
                        autoComplete="new-password"
                        placeholder={t("formRepeatPasswordLabel")}
                        type="password"
                        value={field.state.value}
                        onChange={(event) =>
                          field.handleChange(event.target.value)
                        }
                        onBlur={() => field.handleBlur()}
                      />
                      {getFieldErrorState(field) && (
                        <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                      )}
                    </Field>
                  )}
                />
              </FieldGroup>
              <form.Subscribe
                selector={(state) => [
                  state.isValid,
                  state.isDirty,
                  state.isSubmitting,
                ]}
                children={([isValid, isDirty, isSubmitting]) => (
                  <Button
                    className="w-full"
                    type="submit"
                    disabled={!isDirty || !isValid || isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2
                        className="size-5 animate-spin"
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                    ) : (
                      t("formConfirmNewPasswordButton")
                    )}
                  </Button>
                )}
              />
            </FieldSet>
            <form.Subscribe
              selector={(state) => state.errors}
              children={(errors) =>
                errors.map((error) => (
                  <form.FormError
                    key={error as string}
                    type="error"
                    className="mt-4"
                  >
                    {t(error as string)}
                  </form.FormError>
                ))
              }
            />
          </form>
        )
      }
    />
  );
}

function ResetPasswordLinkExpired() {
  const t = useTranslate();
  const [showSignIn, setShowSignIn] = useState(false);

  if (showSignIn) {
    return <SignInForm header={PageHeading} />;
  }

  return (
    <>
      <Paragraph variant="error" role="alert">
        {t("errorResetPassworCodeExpiredText")}
      </Paragraph>
      <ForgottenPasswordForm
        className="mt-8"
        callback={() => setShowSignIn(true)}
        header={PageHeading}
      />
    </>
  );
}

function ResetPasswordFormContent({
  className,
  ...props
}: ComponentProps<"div">) {
  const { auth } = useAuth();
  const searchParams = useSearchParams();
  const { verifyCode } = useConfirmPasswordReset();
  const oobCode = searchParams.get("oobCode");
  const mode = searchParams.get("mode");
  const [email, setEmail] = useState<string | null>(null);
  const [invalidLink, setInvalidLink] = useState(false);
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    if (mode && mode !== "resetPassword") {
      setInvalidLink(true);
      setVerifying(false);
      return;
    }

    if (!oobCode) {
      setInvalidLink(true);
      setVerifying(false);
      return;
    }

    if (!auth) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const verifiedEmail = await verifyCode(oobCode);
        if (!cancelled) {
          setEmail(verifiedEmail);
          setInvalidLink(false);
        }
      } catch {
        if (!cancelled) {
          setInvalidLink(true);
        }
      } finally {
        if (!cancelled) {
          setVerifying(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [auth, mode, oobCode, verifyCode]);

  const isLoading = verifying || (!auth && !invalidLink);

  return (
    <div className={cn("flex flex-col", className)} {...props}>
      {isLoading ? (
        <ResetPasswordSpinner />
      ) : invalidLink || !oobCode || !email ? (
        <ResetPasswordLinkExpired />
      ) : (
        <ResetPasswordFields oobCode={oobCode} email={email} />
      )}
    </div>
  );
}

export default function ResetPasswordForm(props: ComponentProps<"div">) {
  return (
    <Suspense fallback={<ResetPasswordSpinner className={props.className} />}>
      <ResetPasswordFormContent {...props} />
    </Suspense>
  );
}
